import { useRoute } from '@react-navigation/native';
import React, { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Stack } from '@/components/layout';
import { Loading } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { RelationshipOption } from '@/features/guardian-player/components/invite-guardian/relationship-selector';
import RemoveCoGuardianContent from '@/features/guardian-player/components/remove-co-guardian';
import { useCoGuardianProfile } from '@/features/guardian-player/hooks/use-co-guardian-profile';
import { useRemoveCoGuardianForm } from '@/features/guardian-player/hooks/use-remove-co-guardian-form';
import { REMOVAL_REASON_VALUES } from '@/features/guardian-player/schemas/remove-co-guardian-schema';
import { calculateAge, formatGuardianApiDate } from '@/features/guardian-player/utils';
import { useGuardianStackNavigation } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useRemoveCoGuardianMutation } from '@/queries/use-remove-co-guardian-mutation';
import type { RemovalReason } from '@/services/remove-player-guardian';
import { useUiStore } from '@/stores';

const strings = STRINGS.guardianPlayer.removeCoGuardian;

/** Maps each removal reason to its display label, in the schema's canonical order. */
const REASON_LABELS: Record<RemovalReason, string> = {
  no_longer_co_parenting: strings.reasons.noLongerCoParenting,
  safety_concern: strings.reasons.safetyConcern,
  other: strings.reasons.other,
};

/**
 * Remove Co-Guardian screen (primary guardian only). Reached from the Co-Guardian Profile's
 * Remove action. Confirms the impact, captures a reason + optional detail + acknowledgement,
 * then removes the co-guardian and returns to the Guardian Circle.
 */
const RemoveCoGuardianScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'RemoveCoGuardian'>['route']>();
  const { player, guardianUid } = route.params;

  const { profile, isLoading } = useCoGuardianProfile(player.id, guardianUid);

  const { control, handleSubmit } = useRemoveCoGuardianForm();
  const reason = useWatch({ control, name: 'reason' });
  const acknowledged = useWatch({ control, name: 'acknowledged' });
  const canSubmit = Boolean(reason) && acknowledged === true;

  const { mutateAsync: remove, isPending } = useRemoveCoGuardianMutation(player.id);

  const showGlobalLoading = useUiStore(s => s.showGlobalLoading);
  const hideGlobalLoading = useUiStore(s => s.hideGlobalLoading);

  const reasonOptions = useMemo<RelationshipOption[]>(
    () => REMOVAL_REASON_VALUES.map(value => ({ value, label: REASON_LABELS[value] })),
    [],
  );

  const handleRemove = useMemo(
    () =>
      handleSubmit(async values => {
        if (!guardianUid) return;
        // Block the screen with the global loader while the removal + circle refetch run.
        showGlobalLoading();
        try {
          await remove({
            guardianUid,
            reason: values.reason as RemovalReason,
            detail: values.detail.trim() || undefined,
          });
        } catch {
          hideGlobalLoading();
          return;
        }
        // Turn the loader fully off before navigating, then rebuild the stack so the now-stale
        // Profile / Remove screens are dropped and the (refetched) Guardian Circle is on top.
        hideGlobalLoading();
        navigation.reset({
          index: 2,
          routes: [
            { name: 'GuardianDashboard' },
            { name: 'PlayerDetail', params: { player } },
            { name: 'GuardianCircle', params: { player } },
          ],
        });
      }),
    [handleSubmit, guardianUid, remove, navigation, player, showGlobalLoading, hideGlobalLoading],
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  const coGuardianFullName = profile?.name?.trim() || strings.fallbackName;
  const coGuardianFirstName = coGuardianFullName.split(' ')[0] || coGuardianFullName;

  const kidFirstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const kidFullName =
    [player.firstname, player.lastname].filter(Boolean).join(' ').trim() || kidFirstName;
  const age = calculateAge(player.dob);
  const kidDetailLine = (age != null ? strings.cardDetail : strings.cardDetailNoAge)
    .replace('{age}', String(age))
    .replace('{level}', String(player.level));
  const sinceDate = formatGuardianApiDate(player.createdOn, 'MMM yyyy');
  const kidSinceLine = sinceDate ? strings.cardSince.replace('{date}', sinceDate) : undefined;

  if (isLoading || !profile) {
    return (
      <Stack flex={1} align="center" justify="center">
        <Loading />
      </Stack>
    );
  }

  return (
    <RemoveCoGuardianContent
      screenTitle={strings.screenTitle}
      coGuardianFirstName={coGuardianFirstName}
      coGuardianFullName={coGuardianFullName}
      coGuardianAvatarUrl={profile.avatarUrl}
      coGuardianRoleLabel={profile.roleLabel}
      coGuardianJoinedLabel={profile.joinedLabel}
      kidFirstName={kidFirstName}
      kidFullName={kidFullName}
      kidAvatarUrl={player.avatarUrl}
      kidDetailLine={kidDetailLine}
      kidSinceLine={kidSinceLine}
      reasonOptions={reasonOptions}
      control={control}
      canSubmit={canSubmit}
      onBack={handleBack}
      onRemove={handleRemove}
      removing={isPending}
    />
  );
};

export default RemoveCoGuardianScreen;
